import React, { Fragment, memo, useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import {
  List,
  ListItemText,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  Divider,
} from '@mui/material';

import CloudIcon from '@mui/icons-material/Cloud';
import TimeIcon from '@mui/icons-material/Timelapse';
import NotesIcon from '@mui/icons-material/Assignment';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalysisIcon from '@mui/icons-material/Assessment';
import PrescriptionIcon from '@mui/icons-material/GraphicEq';
import EditIcon from '@mui/icons-material/Edit';

import { getFieldName } from 'js/helpers/StateInterpreters';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { SATELLITE_LAYERS } from 'js/constants/SatelliteLayers';
import { detectBrowser } from 'js/helpers/BrowserDetection';
import { useLangFile } from 'js/context/LanguageContext';
import { useTheme } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import useEvent from '../../hooks/useEvent';
import { saveData } from '../../DownloadFile';
import { blue } from '@mui/material/colors';
import { useHasFeatures } from 'js/context/PermissionContext';
import FeatureConstants from 'js/constants/FeatureConstants';
import useFirebaseAnalytics, { FIREBASE_EVENTS } from '../../hooks/useFirebaseAnalytics';
import { isSatelliteLayer, isSurveyLayer, isYieldLayer } from '../Prescription/PrescriptionJob';
import WebAPIUtils from '../../WebAPIUtils';
import CircularProgress from '@mui/material/CircularProgress';
import { useMapViewContext } from './MapViewContext';
import { Layers } from '@mui/icons-material';

const styles = (theme) => ({
  root: {
    zIndex: 8,
    position: 'relative',
    width: 225,
    '& > div[role="presentation"] > div[role="menu"]': {
      padding: '0 0 10px 0 !important',
    },
  },
  link: {
    color: blue['A700'],
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const getImageTypeName = (imageType) => {
  switch (imageType) {
    case SATELLITE_LAYERS.VITALITY_NDVI:
      return 'ndvi';
    case SATELLITE_LAYERS.VITALITY_NDRE:
      return 'ndre';
    case SATELLITE_LAYERS.VARIATIONS_NDVI:
      return 'ndvi_variation';
    case SATELLITE_LAYERS.VARIATIONS_NDRE:
      return 'ndre_variation';
    case SATELLITE_LAYERS.VISIBLE:
      return 'rgb_photo';
    default:
      return 'ndvi';
  }
};

const FieldPopupContent = ({
  classes,
  field,
  survey,
  fieldImage,
  imageType,
  yieldForField,
  selectedLayer,
}) => {
  const analytics = useFirebaseAnalytics();
  const legacySatellite = useHasFeatures([FeatureConstants.LEGACY_SATELLITE]);
  const [showDebug, setShowDebug] = useState(false);
  const LangFile = useLangFile();
  const Theme = useTheme();
  const [hasNDVIStats, setHasNDVIStats] = useState(false);
  const [fetchingNDVIStats, setFetchingNDVIStats] = useState(false);

  const mapViewContext = useMapViewContext();
  const handleDebugDown = useCallback((event) => {
    if (!event) {
      return;
    }

    if (event.altKey) {
      setShowDebug((val) => !val);
    }
  }, []);

  const onAnalyseClick = useCallback(() => {
    analytics.logEvent(FIREBASE_EVENTS.FPO_ANALYSIS);
    mapViewContext.openAnalysisModule(field);
  }, [field]);

  const onPrescribeClick = useCallback(() => {
    analytics.logEvent(FIREBASE_EVENTS.FPO_VRM);
    mapViewContext.openVRMModule(field);
  }, [field]);

  const onBulkPrescriptionClick = useCallback(() => {
    mapViewContext.openBulkPrescriptionModule(field);
  });

  const onNotesClick = useCallback(() => {
    analytics.logEvent(FIREBASE_EVENTS.FPO_NOTES);
    mapViewContext.openAnalysisModule(field);
  }, [field]);

  const onEditClick = useCallback(() => {
    analytics.logEvent(FIREBASE_EVENTS.FPO_EDIT);
    mapViewContext.openEditModule(field);
  }, [field]);

  const onSettingsClick = useCallback(() => {
    analytics.logEvent(FIREBASE_EVENTS.FPO_SETTINGS);
    mapViewContext.handleOnSettingsClicked(field);
  }, [field]);

  useEvent('keydown', handleDebugDown);

  const ready = field.ready;
  const cloudy = !fieldImage || !(fieldImage[imageType] || fieldImage.url);
  const prescriptionEnabled =
    (legacySatellite && ready && isSatelliteLayer(selectedLayer) && hasNDVIStats) ||
    (survey != null && survey.length > 0 && isSurveyLayer(selectedLayer));
  // As of now it is not possible to make prescriptions from yield maps, so we disable it
  // (isYieldLayer(selectedLayer) && yieldForField != null);
  const analysisEnabled =
    prescriptionEnabled ||
    (isYieldLayer(selectedLayer) && yieldForField != null && yieldForField.length > 0);
  const placeholderIcon = <div style={{ width: '16.8px', height: '18.9px' }} />;
  const warningIcon = <CloudIcon color={'secondary'} />;
  const readyIcon = <TimeIcon color={'secondary'} />;
  const imageName =
    getFieldName(field, LangFile) +
    '_' +
    mapViewContext.date +
    '_' +
    getImageTypeName(imageType) +
    '.png';
  const browser = detectBrowser();

  const downloadHandler = useCallback(async () => {
    analytics.logEvent(FIREBASE_EVENTS.FPO_DOWNLOAD);
    let url;
    if (isYieldLayer(selectedLayer)) {
      url = yieldForField.find((item) => item.date === mapViewContext.date).url;
      window.location.href = url;
      return;
    } else {
      url = fieldImage.url;
    }
    const response = await fetch(url, { method: 'GET', cache: 'no-cache' });
    const blob = await response.blob();
    saveData(blob, imageName);
  }, [fieldImage, imageName]);

  const downloadHandlerIE = useCallback(() => {
    const image = document.getElementById('hidden-image-download');
    const canvas = document.getElementById('hidden-image-canvas');
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, image.clientWidth, image.clientHeight);
    window.navigator.msSaveBlob(canvas.msToBlob(), imageName);
  }, [fieldImage, imageName]);

  const cloudWarning =
    legacySatellite && ready && cloudy ? (
      <ListItem disabled divider>
        <ListItemIcon>{warningIcon}</ListItemIcon>
        <ListItemText
          primary={LangFile.FieldPopupContent.cloudy}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        />
      </ListItem>
    ) : null;

  const readyWarning =
    legacySatellite && !ready ? (
      <ListItem disabled divider style={{ color: Theme.palette.secondary.main }}>
        <ListItemIcon style={{ width: 42 }}>{readyIcon}</ListItemIcon>
        <ListItemText
          primary={LangFile.FieldPopupContent.notReady}
          style={{ whiteSpace: 'normal' }}
        />
      </ListItem>
    ) : null;

  const downloadImageRow =
    !analysisEnabled || !fieldImage ? null : (
      <TableRow style={{ height: 36 }}>
        <TableCell>{LangFile.FieldPopupContent.image}</TableCell>

        <TableCell>
          {browser.isIE ? (
            <Fragment>
              <div onClick={downloadHandlerIE} className={classes.link}>
                {LangFile.FieldPopupContent.download}
              </div>

              <img
                id={'hidden-image-download'}
                alt="hidden"
                src={fieldImage.url}
                style={{
                  position: 'fixed',
                  top: -9999,
                  left: -9999,
                }}
                tabIndex={-1}
              />
              <canvas
                id={'hidden-image-canvas'}
                style={{ position: 'fixed', top: -9999, left: -9999 }}
                tabIndex={-1}
              />
            </Fragment>
          ) : (
            <div onClick={downloadHandler} className={classes.link}>
              {LangFile.FieldPopupContent.download}
            </div>
          )}
        </TableCell>
      </TableRow>
    );

  useEffect(() => {
    const fetchNDVIStats = async () => {
      if (ready && isSatelliteLayer(selectedLayer) && field?.bundle) {
        try {
          setFetchingNDVIStats(true);
          const result = await WebAPIUtils.getBundleStats(field.bundle, 'ndvi');
          setHasNDVIStats(Array.isArray(result) && result.length > 0);
          setFetchingNDVIStats(false);
        } catch (error) {
          console.error('Failed to fetch NDVI stats:', error);
          setHasNDVIStats(false);
          setFetchingNDVIStats(false);
        }
      } else {
        setHasNDVIStats(false);
      }
    };

    fetchNDVIStats();
  }, [ready, selectedLayer, field?.bundle]);

  return (
    <div className={classes.root}>
      <List dense>
        {fetchingNDVIStats ? (
          <CircularProgress style={{ width: 'auto', display: 'flex', justifyContent: 'center' }} />
        ) : (
          <>
            <ListSubheader disableSticky>{LangFile.FieldPopupContent.shortcuts}</ListSubheader>

            <ListItemButton disabled={!analysisEnabled} onClick={onAnalyseClick} dense>
              <ListItemIcon>
                <AnalysisIcon color={'action'} />
              </ListItemIcon>
              <ListItemText primary={LangFile.FieldPopupContent.analyse} />
            </ListItemButton>

            <ListItemButton
              data-testid={'start-prescription'}
              onClick={onPrescribeClick}
              disabled={!prescriptionEnabled}
              dense
            >
              <ListItemIcon>
                <PrescriptionIcon color={'action'} />
              </ListItemIcon>
              <ListItemText primary={LangFile.FieldPopupContent.prescribe} />
              <ListItemSecondaryAction>
                {legacySatellite && ready && cloudy ? warningIcon : placeholderIcon}
              </ListItemSecondaryAction>
            </ListItemButton>

            <ListItemButton
              data-testid="start-bulk-prescription"
              onClick={onBulkPrescriptionClick}
              disabled={!prescriptionEnabled}
              dense
            >
              <ListItemIcon>
                <Layers color={'action'} />
              </ListItemIcon>
              <ListItemText primary={LangFile.FieldPopupContent.bulkPrescription} />
              <ListItemSecondaryAction>
                {legacySatellite && ready && cloudy ? warningIcon : placeholderIcon}
              </ListItemSecondaryAction>
            </ListItemButton>

            <ListItemButton onClick={onNotesClick} disabled={!analysisEnabled} dense>
              <ListItemIcon>
                <NotesIcon color={'action'} />
              </ListItemIcon>
              <ListItemText primary={LangFile.FieldPopupContent.notes} />
              <ListItemSecondaryAction>
                {legacySatellite && !ready ? readyIcon : placeholderIcon}
              </ListItemSecondaryAction>
            </ListItemButton>

            <ListItemButton onClick={onEditClick} dense>
              <ListItemIcon>
                <EditIcon color={'action'} />
              </ListItemIcon>
              <ListItemText primary={LangFile.FieldPopupContent.edit} />
            </ListItemButton>

            <ListItemButton onClick={onSettingsClick} divider dense>
              <ListItemIcon>
                <SettingsIcon color={'action'} />
              </ListItemIcon>
              <ListItemText primary={LangFile.FieldPopupContent.manage} />
            </ListItemButton>

            {cloudWarning}
            {cloudWarning && <Divider />}

            {readyWarning}
            {readyWarning && <Divider />}
          </>
        )}
      </List>
      <Table size={'small'}>
        <TableBody>
          {showDebug && (
            <TableRow style={{ height: 36 }}>
              <TableCell>{LangFile.FieldPopupContent.bundleId}</TableCell>
              <TableCell>{field.bundle || '–'}</TableCell>
            </TableRow>
          )}
          {showDebug && (
            <TableRow style={{ height: 36 }}>
              <TableCell>{LangFile.FieldPopupContent.fieldId}</TableCell>
              <TableCell>{field.fieldId}</TableCell>
            </TableRow>
          )}
          <TableRow style={{ height: 36 }}>
            <TableCell>{LangFile.FieldPopupContent.name}</TableCell>
            <TableCell style={{ whiteSpace: 'break-spaces' }}>
              {field.name || field.mef || '-'}
            </TableCell>
          </TableRow>
          <TableRow style={{ height: 36 }}>
            <TableCell>{LangFile.FieldPopupContent.area}</TableCell>
            <TableCell>{field.size.toFixed(1) + ' ha'}</TableCell>
          </TableRow>
          {downloadImageRow}
        </TableBody>
      </Table>
    </div>
  );
};

FieldPopupContent.propTypes = {
  fieldImage: PropTypes.object,
  LangFile: PropTypes.object,
  field: PropTypes.object,
};

export default memo(withStyles(styles)(FieldPopupContent));
